import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';

type Mailboxes = Awaited<ReturnType<typeof api.get<'/api/v1/mailboxes'>>>;
type Run = Awaited<ReturnType<typeof api.post<'/api/v1/rescue'>>>;
type Detail = Awaited<ReturnType<typeof api.get<'/api/v1/rescue/{runId}'>>>;

const ACTIVE = new Set(['planning', 'awaiting_backup', 'ready', 'running', 'paused']);

export function RescueScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { activeWorkspace } = useAuth();
  const tenantId = activeWorkspace?.id;
  const onboardingReturn = new URLSearchParams(window.location.search).get('onboarding') === '1';
  const [mailboxes, setMailboxes] = useState<Mailboxes | null>(null);
  const [mailboxId, setMailboxId] = useState('');
  const [run, setRun] = useState<Run | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const loadDetail = useCallback(
    async (selectedRun: Run, signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      const next = await api.get(
        '/api/v1/rescue/{runId}',
        { runId: selectedRun.id },
        { tenantId, signal },
      );
      setRun(next.run);
      setDetail(next);
    },
    [tenantId],
  );

  const loadMailboxes = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        const next = await api.get('/api/v1/mailboxes', undefined, { tenantId, signal });
        setMailboxes(next);
        setMailboxId((current) =>
          next.mailboxes.some((mailbox) => mailbox.id === current)
            ? current
            : (next.mailboxes[0]?.id ?? ''),
        );
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    setMailboxes(null);
    setMailboxId('');
    setRun(null);
    setDetail(null);
    void loadMailboxes(controller.signal);
    return () => controller.abort();
  }, [loadMailboxes]);

  const loadRun = useCallback(
    async (selectedMailboxId: string, signal?: AbortSignal) => {
      if (tenantId === undefined || selectedMailboxId === '') return;
      try {
        const recent = await api.get(
          '/api/v1/rescue',
          { mailboxId: selectedMailboxId, limit: 20 },
          { tenantId, signal },
        );
        const newest = recent.runs[0] ?? null;
        setRun(newest);
        if (newest !== null) await loadDetail(newest, signal);
        else setDetail(null);
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
      }
    },
    [loadDetail, tenantId],
  );

  useEffect(() => {
    if (mailboxId === '') return;
    const controller = new AbortController();
    setRun(null);
    setDetail(null);
    void loadRun(mailboxId, controller.signal);
    return () => controller.abort();
  }, [loadRun, mailboxId]);

  useEffect(() => {
    if (run === null || !ACTIVE.has(run.status)) return;
    let cancelled = false;
    let timer: number | undefined;

    const schedule = (delay: number) => {
      timer = window.setTimeout(() => void refresh(), delay);
    };
    const refresh = async () => {
      try {
        await loadDetail(run);
        if (!cancelled) {
          setFailure(null);
          schedule(10_000);
        }
      } catch (error) {
        if (cancelled) return;
        setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
        // A failed status update must never turn into a tight retry loop. Honour the server's
        // Retry-After signal, then retry at the normal gentle polling cadence.
        const retryAfter = error instanceof ApiError ? error.problem.retryAfterSeconds : undefined;
        schedule(Math.max(10_000, (retryAfter ?? 10) * 1_000));
      }
    };

    schedule(10_000);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [loadDetail, run]);

  const start = async () => {
    if (tenantId === undefined || mailboxId === '') return;
    setBusy(true);
    try {
      const started = await api.post('/api/v1/rescue', { mailboxId, mode: 'analyse' }, undefined, {
        tenantId,
      });
      setRun(started);
      await loadDetail(started);
      if (onboardingReturn) {
        await api.post(
          '/api/v1/onboarding/steps',
          { step: 'rescue_start', skipped: false },
          undefined,
          { tenantId },
        );
        window.location.assign('/welcome');
      }
      setFailure(null);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
    } finally {
      setBusy(false);
    }
  };

  const control = async (action: 'pause' | 'resume' | 'cancel') => {
    if (tenantId === undefined || run === null) return;
    setBusy(true);
    try {
      const updated = await (action === 'pause'
        ? api.post('/api/v1/rescue/{runId}/pause', {}, { runId: run.id }, { tenantId })
        : action === 'resume'
          ? api.post('/api/v1/rescue/{runId}/resume', {}, { runId: run.id }, { tenantId })
          : api.post('/api/v1/rescue/{runId}/cancel', {}, { runId: run.id }, { tenantId }));
      setRun(updated);
      await loadDetail(updated);
      setFailure(null);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.code : 'UNREACHABLE');
    } finally {
      setBusy(false);
    }
  };

  if (mailboxes === null) {
    if (failure !== null) {
      return (
        <>
          <Alert tone="critical">{t(`error.${failure}`)}</Alert>
          <Button onClick={() => void loadMailboxes()}>{t('common.retry')}</Button>
        </>
      );
    }
    return <div className="boot">{t('common.loading')}</div>;
  }
  const selectedMailbox = mailboxes.mailboxes.find((mailbox) => mailbox.id === mailboxId);
  const staleCompletedRun =
    run?.status === 'completed' &&
    selectedMailbox !== undefined &&
    selectedMailbox.indexedMessageCount > run.totalMessages;
  const mayStart =
    selectedMailbox !== undefined &&
    selectedMailbox.vaultVerified &&
    (run === null || !ACTIVE.has(run.status));

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('rescue.title')}</h1>
        <p className="empty__body">{t('rescue.intro')}</p>
      </header>

      {failure !== null && <Alert tone="critical">{t(`error.${failure}`)}</Alert>}

      {mailboxes.mailboxes.length === 0 ? (
        <Alert tone="attention">{t('rescue.noMailbox')}</Alert>
      ) : (
        <section className="card">
          <label className="field-group__label" htmlFor="rescue-mailbox">
            {t('rescue.mailbox')}
          </label>
          <select
            className="select"
            id="rescue-mailbox"
            value={mailboxId}
            onChange={(event) => {
              setMailboxId(event.target.value);
              setRun(null);
              setDetail(null);
            }}
          >
            {mailboxes.mailboxes.map((mailbox) => (
              <option key={mailbox.id} value={mailbox.id}>
                {mailbox.emailAddress}
              </option>
            ))}
          </select>

          {!mayStart && run === null && <Alert tone="info">{t('rescue.waitForSnapshot')}</Alert>}

          {staleCompletedRun && (
            <Alert tone="info">
              {t('rescue.newMessagesIndexed', { count: selectedMailbox.indexedMessageCount })}
            </Alert>
          )}

          <div className="mailbox-form__actions">
            <Button
              variant="primary"
              disabled={!mayStart || busy}
              loading={busy}
              onClick={() => void start()}
            >
              {staleCompletedRun
                ? t('rescue.startUpdatedAnalysis', {
                    count: selectedMailbox?.indexedMessageCount ?? 0,
                  })
                : t('rescue.startAnalysis')}
            </Button>
            {run !== null && (run.status === 'running' || run.status === 'ready') && (
              <Button disabled={busy} onClick={() => void control('pause')}>
                {t('rescue.pause')}
              </Button>
            )}
            {run?.status === 'paused' && (
              <Button disabled={busy} onClick={() => void control('resume')}>
                {t('rescue.resume')}
              </Button>
            )}
            {run !== null && ACTIVE.has(run.status) && (
              <Button variant="danger" disabled={busy} onClick={() => void control('cancel')}>
                {t('rescue.cancel')}
              </Button>
            )}
          </div>
        </section>
      )}

      {run !== null && detail !== null && (
        <section className="card" aria-live="polite">
          <h2 className="card__title">{t('rescue.status.title')}</h2>
          <span className="badge badge--info">{t(`rescue.status.${run.status}`)}</span>
          <p className="empty__body">
            {t('rescue.progress', {
              processed: detail.progress.processedMessages,
              total: detail.progress.totalMessages,
            })}
          </p>
          <progress
            max={Math.max(detail.progress.totalMessages, 1)}
            value={detail.progress.processedMessages}
          />

          <h3 className="card__title">{t('rescue.findings')}</h3>
          {detail.findings.length === 0 ? (
            <p className="empty__body">{t('rescue.findings.empty')}</p>
          ) : (
            <ul className="today-list">
              {detail.findings.map((finding) => (
                <li className="today-list__item" key={finding.id}>
                  <span className="badge badge--info">{t(`rescue.finding.${finding.kind}`)}</span>
                  <span className="today-list__subject">{finding.subject}</span>
                  <span className="today-list__meta">
                    {t('rescue.finding.count', { count: finding.messageCount })}
                    {finding.bytesTotal > 0
                      ? ` · ${new Intl.NumberFormat(locale, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 }).format(finding.bytesTotal / 1_048_576)}`
                      : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
